import {omit, has, some, pick, take} from 'lodash';
import type {BreakpointID} from '@packages/systems/style/types';
import {
  constantFalse,
  identity,
  emptyObject,
} from '@packages/utilities/fp/utils';
import {getOrderedBreakpointIds} from '@packages/systems/style/media-queries';
import {
  matchExpression,
  Expressions,
  type ExpressionPath,
  Plugins,
  Component,
} from '@packages/systems/wfdl';
import {
  type ExpressionElement,
  type Expression,
  EElement,
  ERecord,
  EText,
  ENumber,
  EList,
  EEnum,
} from '@packages/systems/wfdl-ast';
import {PSEUDO_KEYS} from '@packages/systems/core/utils/StyleUtils';
import type {PseudoStateKey} from '@packages/systems/core/utils/StyleTypes';
import {StylePath} from '@packages/systems/style-legacy/values';
import {
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
} from '@packages/systems/style/breakpoint-ids';
import {generateGuid} from '@packages/utilities/guid';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {getSecond} from '@packages/utilities/Pair';
import {getAppliedElementStyle} from '@packages/shared/utils/getAppliedNodeStyle';
import {SpanPlacement} from '@packages/systems/grid';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';
import {updateElementDataWithTyped} from '@packages/utilities/platform-elementdata';
import {TEMP_PATH} from '@packages/utilities/expression-path-constants';

const STYLE_BASE_PATH: ExpressionPath = [{in: 'Record', at: 'base'}];
const STYLE_VARIANT_PATH: ExpressionPath = [{in: 'Record', at: 'variants'}];

const breakpointsStylePathMap: Partial<Record<BreakpointID, ExpressionPath>> = {
  [BREAKPOINT_ID_XXL]: [{in: 'Record', at: BREAKPOINT_ID_XXL}],
  [BREAKPOINT_ID_XL]: [{in: 'Record', at: BREAKPOINT_ID_XL}],
  [BREAKPOINT_ID_LARGE]: [{in: 'Record', at: BREAKPOINT_ID_LARGE}],
  [BREAKPOINT_ID_MAIN]: [{in: 'Record', at: BREAKPOINT_ID_MAIN}],
  [BREAKPOINT_ID_MEDIUM]: [{in: 'Record', at: BREAKPOINT_ID_MEDIUM}],
  [BREAKPOINT_ID_SMALL]: [{in: 'Record', at: BREAKPOINT_ID_SMALL}],
  [BREAKPOINT_ID_TINY]: [{in: 'Record', at: BREAKPOINT_ID_TINY}],
};

const generateStylePath = (
  breakpointId: BreakpointID,
  pseudoKey: PseudoStateKey,
  variantState?: VariantStateKey | null
): ExpressionPath => {
  const variantOrBasePath: ExpressionPath = variantState
    ? [...STYLE_VARIANT_PATH, {in: 'Record', at: variantState}]
    : STYLE_BASE_PATH;

  const breakpointPath =
    breakpointsStylePathMap[breakpointId] ??
    breakpointsStylePathMap[BREAKPOINT_ID_MAIN]!;

  return [
    {in: 'Record', at: 'style'},
    ...variantOrBasePath,
    ...breakpointPath,
    {in: 'Record', at: pseudoKey},
  ] satisfies ExpressionPath;
};

const defaultNoGridItemStyle: Record<string, unknown> = {
  gridArea: null,
  gridColumnStart: null,
  gridColumnEnd: null,
  gridRowStart: null,
  gridRowEnd: null,
  justifySelf: null,
  alignSelf: null,
  order: null,
};

const gridItemPropertyKeys = Object.keys(defaultNoGridItemStyle);

type GridStyleKey =
  | 'gridColumnStart'
  | 'gridColumnEnd'
  | 'gridRowStart'
  | 'gridRowEnd'
  | 'gridArea';

const hasAnyGridItemStyleKey = (anyObject: {
  [key: string]: unknown;
}): boolean => {
  return some(gridItemPropertyKeys, (key) => has(anyObject, key));
};

export const updateElementDataLocalStyle =
  (
    payload: Record<string, unknown>,
    breakpointId?: BreakpointID | null,
    pseudoMode?: PseudoStateKey | null,
    variantState?: VariantStateKey | null
  ) =>
  (data: Expression) => {
    if (!breakpointId) breakpointId = BREAKPOINT_ID_MAIN;
    let pseudoKey: PseudoStateKey = pseudoMode ? pseudoMode : 'noPseudo';

    // TODO: Remove this after local style feature is fully implemented in style tab
    if (
      pseudoKey === 'hover' &&
      // Users are accidentally applying grid styles on hover without realizing it, resulting
      // in their elements changing position unexpectedly when their mouse is over the element.
      // 99% use case does not have positions changing on hover, so changes to item positioning
      // defaults to the base no pseudo style.
      hasAnyGridItemStyleKey(payload)
    ) {
      pseudoKey = 'noPseudo';
    }

    const pathToUpdate = generateStylePath(
      breakpointId,
      pseudoKey,
      variantState
    );

    // We are using sparseUpdateIn here as styles are not
    // guaranteed to exist on the element.
    return Expressions.sparseUpdateIn(
      data,
      pathToUpdate,
      (expressionOrVoid) => {
        // If the path segment doesn't exist, simply return a new record
        if (!expressionOrVoid) {
          return ERecord(getPayloadFields(payload));
        }

        return matchExpression({
          Record: (fields) => {
            return ERecord(getPayloadFields(payload, fields));
          },
          _: identity,
        })(expressionOrVoid);
      }
    );
  };

export const updateElementLocalStyle = (
  payload: Record<string, unknown>,
  breakpointId?: BreakpointID | null,
  pseudoMode?: PseudoStateKey | null,
  variantState?: VariantStateKey | null
) =>
  updateElementDataWithTyped(
    updateElementDataLocalStyle(payload, breakpointId, pseudoMode, variantState)
  );

const getPayloadFields = (
  payload: Record<string, unknown>,
  initialFields?: {
    [key: string]: Expression;
  }
) => {
  // We pass a shallow clone of the initial fields to the
  // reduce function to avoid accidentally mutating the initial object
  const initial = Object.assign({}, initialFields);

  return Object.keys(payload).reduce((acc, curr) => {
    if (typeof payload[curr] === 'string') {
      acc[curr] = EText(payload[curr]);
    } else if (typeof payload[curr] === 'number') {
      acc[curr] = ENumber(payload[curr]);
    } else if (payload[curr] === null) {
      return omit(acc, [curr]);
    }

    return acc;
  }, initial);
};

const hasGridProperty = matchExpression({
  Record: hasAnyGridItemStyleKey,
  _: constantFalse,
});

export const clearGridItemStylesAllQueries =
  (breakpointIds: BreakpointID[]) =>
  (variantState: VariantStateKey | null) =>
  (element: ExpressionElement): ExpressionElement => {
    return breakpointIds.reduce(
      (outerElement, breakpointId) =>
        PSEUDO_KEYS.reduce((innerElement, pseudoMode) => {
          const elementStyle = Expressions.getIn(
            Expressions.getElementData(innerElement),
            generateStylePath(breakpointId, pseudoMode)
          );

          return elementStyle && hasGridProperty(elementStyle)
            ? updateElementLocalStyle(
                defaultNoGridItemStyle,
                breakpointId,
                pseudoMode,
                variantState
              )(innerElement)
            : innerElement;
        }, outerElement),
      element
    );
  };

export type GridItemStyle = {
  gridArea?: null | string;
  gridColumnStart?: null | string | number;
  gridColumnEnd?: null | string | number;
  gridRowStart?: null | string | number;
  gridRowEnd?: null | string | number;
  justifySelf?: null | string;
  alignSelf?: null | string;
};

const getGridStyleValue = (
  styleExpr: Expression,
  styleKey: GridStyleKey
): number | null =>
  matchExpression({
    Record: (fields) => {
      const numExpr = fields[styleKey];
      if (!numExpr) return null;
      const val = Expressions.getNumber(numExpr);
      return Number.isFinite(val) ? val : null;
    },
    _: () => null,
  })(styleExpr);

export const setPastedGridItemStyleForElement = (
  element: ExpressionElement
): ExpressionElement => {
  const newElement = getOrderedBreakpointIds().reduce(
    (outerElement, breakpointId) => {
      return PSEUDO_KEYS.reduce((innerElement, pseudoMode) => {
        const elementStyle = Expressions.getIn(
          Expressions.getElementData(innerElement),
          generateStylePath(breakpointId, pseudoMode)
        );

        if (!elementStyle || !hasGridProperty(elementStyle)) {
          return innerElement;
        }

        const gridColumnStart = getGridStyleValue(
          elementStyle,
          'gridColumnStart'
        );
        const gridColumnEnd = getGridStyleValue(elementStyle, 'gridColumnEnd');
        const gridRowStart = getGridStyleValue(elementStyle, 'gridRowStart');
        const gridRowEnd = getGridStyleValue(elementStyle, 'gridRowEnd');

        // Let autoplaced item flow — no need to place in top left cell...
        const isAutoPlaced = isGridItemAutoPlaced({
          gridColumnStart,
          gridColumnEnd,
          gridRowStart,
          gridRowEnd,
        });

        return isAutoPlaced
          ? innerElement
          : updateElementLocalStyle(
              {
                gridColumnStart: 1,
                gridRowStart: 1,
                gridColumnEnd:
                  (gridColumnEnd || 0) - (gridColumnStart || 0) + 1,
                gridRowEnd: (gridRowEnd || 0) - (gridRowStart || 0) + 1,
              },
              breakpointId,
              pseudoMode
            )(innerElement);
      }, outerElement);
    },
    element
  );

  return newElement;
};

type GridItemPlacement = {
  gridRowStart?: null | number | string;
  gridRowEnd?: null | number | string;
  gridColumnStart?: null | number | string;
  gridColumnEnd?: null | number | string;
  gridArea?: null | string;
};

export const isGridItemAutoPlaced = (
  itemPlacement: GridItemPlacement
): boolean =>
  !isGridItemManuallyPlaced(itemPlacement) &&
  !isGridItemAreaPlaced(itemPlacement);

export const isGridItemManuallyPlaced = ({
  gridRowStart,
  gridRowEnd,
  gridColumnStart,
  gridColumnEnd,
  gridArea,
}: GridItemPlacement): boolean =>
  gridArea == null &&
  typeof gridRowStart === 'number' &&
  typeof gridRowEnd === 'number' &&
  typeof gridColumnStart === 'number' &&
  typeof gridColumnEnd === 'number';

export const isGridItemAreaPlaced = ({
  gridRowStart,
  gridRowEnd,
  gridColumnStart,
  gridColumnEnd,
  gridArea,
}: GridItemPlacement): boolean =>
  gridArea != null &&
  gridRowStart == null &&
  gridRowEnd == null &&
  gridColumnStart == null &&
  gridColumnEnd == null;

export const filterGridItemStyles = (
  breakpointIds: BreakpointID[],
  element: ExpressionElement
): ExpressionElement => {
  const updatedElement = breakpointIds.reduce(
    (outerElement, breakpointId) =>
      PSEUDO_KEYS.reduce((innerElement, pseudoMode) => {
        const elementStyle = Expressions.getIn(
          Expressions.getElementData(innerElement),
          generateStylePath(breakpointId, pseudoMode)
        );

        if (elementStyle) {
          const recordFields = Expressions.getRecord(elementStyle);
          if (recordFields == null) return innerElement;

          const nextElementStyle = pick(recordFields, gridItemPropertyKeys);
          if (nextElementStyle && Object.keys(nextElementStyle).length === 0)
            return innerElement;

          return hasGridProperty(elementStyle)
            ? updateElementLocalStyle(
                nextElementStyle,
                breakpointId,
                pseudoMode
              )(innerElement)
            : innerElement;
        }

        return innerElement;
      }, outerElement),
    element
  );

  return updatedElement;
};

export function updateGridRowData(
  elementData: Expression,
  {
    mediaQuery,
    newConfig,
    temp = false,
  }: {
    mediaQuery: BreakpointID;
    newConfig: string;
    temp?: boolean;
  }
): Expression {
  let updatedData = elementData;

  if (mediaQuery === BREAKPOINT_ID_MAIN) {
    if (temp) {
      updatedData = Expressions.sparseSetIn(
        updatedData,
        [
          ...TEMP_PATH,
          {in: 'Record', at: 'grid'},
          {in: 'Record', at: 'cols'},
          {in: 'Record', at: BREAKPOINT_ID_MAIN},
        ],
        EText(newConfig)
      );
    } else {
      updatedData = Expressions.sparseSetIn(
        updatedData,
        [
          {
            in: 'Record',
            at: 'grid',
          },
          {
            in: 'Record',
            at: 'cols',
          },
        ],
        ERecord({
          main: EText(newConfig),
          medium: EText(''),
          small: EText(''),
          tiny: EText(''),
        })
      );
    }
  } else {
    // Override mediaQuery specific cols
    updatedData = Expressions.sparseSetIn(
      updatedData,
      [
        {
          in: 'Record',
          at: 'grid',
        },
        {
          in: 'Record',
          at: 'cols',
        },
        {
          in: 'Record',
          at: mediaQuery,
        },
      ],
      EText(newConfig)
    );
  }

  // Remove any temporary grid data if we're not in temp mode
  if (!temp) {
    return Expressions.deleteInRecord(updatedData, [
      ...TEMP_PATH,
      {in: 'Record', at: 'grid'},
    ]);
  }

  return updatedData;
}

export const setDefaultNoGridItemStyle = (
  mediaQuery: BreakpointID,
  pseudoMode: null | PseudoStateKey,
  variantState: VariantStateKey | null,
  property: 'gridItemPosition' | 'alignItem' | 'order'
) => {
  const propertyPath = StylePath.getPath(property);

  if (typeof propertyPath === 'string') {
    const styleProp = defaultNoGridItemStyle[propertyPath];
    return styleProp !== undefined
      ? updateElementDataLocalStyle(
          {[propertyPath]: styleProp},
          mediaQuery,
          pseudoMode,
          variantState
        )
      : identity;
  }

  const resetStyles = Object.keys(propertyPath).reduce<Record<string, unknown>>(
    (acc, key) => {
      const styleProp = defaultNoGridItemStyle[key];
      if (styleProp !== undefined) acc[key] = styleProp;
      return acc;
    },
    {}
  );
  return updateElementDataLocalStyle(
    resetStyles,
    mediaQuery,
    pseudoMode,
    variantState
  );
};

export const getGridElementsToModify = function (payload: {
  rowElement: ExpressionElement;
  mediaQuery: BreakpointID;
  newConfig: string;
  resizePreview: boolean | void;
}): {
  childrenToAdd: Array<Expression> | null;
  childrenAfterDelete: Array<Expression> | null;
} {
  const {rowElement, mediaQuery, newConfig, resizePreview} = payload;

  // Change the grid structure
  if (mediaQuery === 'main') {
    let oldConfigExpr = Expressions.getIn(
      Expressions.getElementData(rowElement),
      [
        {in: 'Record', at: 'grid'},
        {in: 'Record', at: 'cols'},
        {in: 'Record', at: 'main'},
      ]
    );

    if (resizePreview) {
      const tempConfigExpr = Expressions.getIn(
        Expressions.getElementData(rowElement),
        [
          ...TEMP_PATH,
          {in: 'Record', at: 'grid'},
          {in: 'Record', at: 'cols'},
          {in: 'Record', at: 'main'},
        ]
      );
      if (tempConfigExpr && Expressions.getText(tempConfigExpr)) {
        oldConfigExpr = tempConfigExpr;
      }
    }

    const oldConfig = oldConfigExpr ? Expressions.getText(oldConfigExpr) : '';

    const oldCount = parseCols(oldConfig || '').length;
    const newCount = parseCols(newConfig).length;
    const colsToAdd = newCount > oldCount ? newCount - oldCount : 0;
    const colsToArchive = newCount < oldCount ? oldCount - newCount : 0;

    if (oldCount === newCount) {
      // A resize operation, where the number of columns is not changing
      // should not create or delete elements
    } else if (colsToAdd) {
      // Add new columns
      const newElements: Expression[] = [];
      for (let i = 0; i < colsToAdd; i++) {
        newElements.push(
          getSecond(
            instantiateExpressionWithUniqueIds(
              EElement({
                id: 'Column',
                type: qualify(Plugins.name('Layout'), Component.name('Column')),
                data: ERecord({
                  tag: EEnum('div'),
                  grid: ERecord({
                    type: EText('col'),
                  }),
                  children: EList([]),
                }),
              })
            )(generateGuid())
          )
        );
      }
      return {
        childrenAfterDelete: null,
        childrenToAdd: newElements,
      };
    } else if (colsToArchive) {
      const elChildrenExpr = Expressions.getIn(
        Expressions.getElementData(rowElement),
        [{in: 'Record', at: 'children'}]
      );
      const children = elChildrenExpr
        ? Expressions.getList(elChildrenExpr)
        : [];
      return {
        childrenAfterDelete: children
          ? take(children, children.length - colsToArchive)
          : [],
        childrenToAdd: null,
      };
    }
  }
  return {
    childrenAfterDelete: null,
    childrenToAdd: null,
  };
};

/**
 * A generic description of a 2D region within a CSS Grid.
 * Much like a `Rect`, but specified with row/columnStart/End
 */
type GridRegion = {
  rowStart: number;
  rowEnd: number;
  columnStart: number;
  columnEnd: number;
};

type GridSpanStyles = {
  gridRowStart?: number;
  gridRowEnd?: number;
  gridColumnStart?: number;
  gridColumnEnd?: number;
};

type SpanStylesType =
  | GridSpanStyles
  | {
      gridRowStart: string;
      gridRowEnd: string;
      gridColumnStart: string;
      gridColumnEnd: string;
    };

export const getGridSpanStyles = (
  cellProbe: GridRegion,
  corner: SpanPlacement
): GridSpanStyles => {
  const newRowEnd = cellProbe.rowStart + 1;
  const newColumnEnd = cellProbe.columnStart + 1;
  const newColumnStart = cellProbe.columnStart;
  const newRowStart = cellProbe.rowStart;

  switch (corner) {
    case 'top': {
      return {gridRowStart: newRowStart};
    }
    case 'right': {
      return {gridColumnEnd: newColumnEnd};
    }
    case 'bottom': {
      return {gridRowEnd: newRowEnd};
    }
    case 'left': {
      return {gridColumnStart: newColumnStart};
    }
    case 'topRight': {
      return {gridRowStart: newRowStart, gridColumnEnd: newColumnEnd};
    }
    case 'topLeft': {
      return {gridRowStart: newRowStart, gridColumnStart: newColumnStart};
    }
    case 'bottomRight': {
      return {gridRowEnd: newRowEnd, gridColumnEnd: newColumnEnd};
    }
    case 'bottomLeft': {
      return {gridRowEnd: newRowEnd, gridColumnStart: newColumnStart};
    }
  }

  return emptyObject;
};

// E.g. prevents use from dragging from left side and dropping over on
// the right side PAST the column end
export const isWithinSpanBounds = (
  corner: SpanPlacement,
  cellProbe: GridRegion,
  rowStart: number,
  rowEnd: number,
  columnStart: number,
  columnEnd: number
) => {
  const probeRowStart = cellProbe.rowStart;
  const probeColumnStart = cellProbe.columnStart;
  const probeRowEnd = cellProbe.rowEnd;
  const probeColumnEnd = cellProbe.columnEnd;

  switch (corner) {
    case 'top': {
      return probeRowEnd <= rowEnd;
    }

    case 'right': {
      return probeColumnStart >= columnStart;
    }

    case 'bottom': {
      return probeRowStart >= rowStart;
    }

    case 'left': {
      return probeColumnEnd <= columnEnd;
    }

    case 'bottomRight': {
      return probeColumnStart >= columnStart && probeRowStart >= rowStart;
    }

    case 'bottomLeft': {
      return probeColumnEnd <= columnEnd && probeRowStart >= rowStart;
    }

    case 'topRight': {
      return probeColumnStart >= columnStart && probeRowEnd <= rowEnd;
    }

    case 'topLeft': {
      return probeColumnEnd <= columnEnd && probeRowEnd <= rowEnd;
    }

    default:
      return false;
  }
};

export const spanGridItem =
  (
    mediaQuery: BreakpointID,
    pseudoMode: null | PseudoStateKey,
    variantState: null | VariantStateKey,
    hoveredCell: {
      rowStart: number;
      columnStart: number;
      rowEnd: number;
      columnEnd: number;
    },
    corner: SpanPlacement,
    itemLocation: {
      gridRowStart: number;
      gridColumnStart: number;
      gridRowEnd: number;
      gridColumnEnd: number;
    }
  ) =>
  (element: ExpressionElement): ExpressionElement => {
    const elementStyle = getAppliedElementStyle(
      element,
      mediaQuery,
      pseudoMode,
      variantState
    );

    if (
      !isWithinSpanBounds(
        corner,
        hoveredCell,
        itemLocation.gridRowStart,
        itemLocation.gridRowEnd,
        itemLocation.gridColumnStart,
        itemLocation.gridColumnEnd
      )
    ) {
      return element;
    }

    const gridSpanStyles = getGridSpanStyles(hoveredCell, corner);
    let spanStyles: SpanStylesType = gridSpanStyles;

    if (isGridItemAutoPlaced(elementStyle)) {
      const manualPlacement = {...itemLocation, ...gridSpanStyles} as const;
      const rowSpan = `span ${
        manualPlacement.gridRowEnd - manualPlacement.gridRowStart
      }`;
      const columnSpan = `span ${
        manualPlacement.gridColumnEnd - manualPlacement.gridColumnStart
      }`;

      spanStyles = {
        gridRowStart: rowSpan,
        gridRowEnd: rowSpan,
        gridColumnStart: columnSpan,
        gridColumnEnd: columnSpan,
      };
    }

    const newStyle = {...elementStyle, ...spanStyles} as const;
    return updateElementLocalStyle(
      newStyle,
      mediaQuery,
      pseudoMode,
      variantState
    )(element);
  };

export function parseCols(colString: string): number[] {
  return colString.split('|').map((colWidth) => parseInt(colWidth, 10));
}
